// "What we bought": every number on the page, from the receipts list
// (/receipts-data/list?withLines=1). Pure; dollars in, dollars out, summed in
// cents so nothing drifts.
//
// Honest by construction:
//   - kind totals are items before tax. A line's amount is what was paid for
//     it; its printed discount comes off only when that receipt's own check
//     forms say its lines are gross (server receipts-checks.js itemsBasis,
//     mirrored here). Tax is never spread over kinds; a receipt discount on
//     no line is reported apart as unallocated
//   - for every receipt that checked out: items − unallocated + tax + tip =
//     its total (reconcile() shows it on the page)
//   - an unlabeled line counts as "other", never dropped
//   - a receipt with no lines read still counts in totals and receipt counts;
//     it is reported as unlabeled dollars, not hidden
//   - a payment (rowKind 'payment': a tax payment, a fee, rent) is not a
//     purchase: the spending figures run on purchases (splitPayments), and
//     payments get their own section
//   - only item lines are items: a payment's synthetic line and a tax printed
//     as its own line (isTax: a hotel's COUNTY TAX) never count as a kind

import { KINDS, KIND_GROUPS, kindOf, groupOfKind } from './kindGroups';
import { cardText, payerName } from './cardText';
import { itemLabel } from './itemName';

const c = (d) => (typeof d === 'number' && Number.isFinite(d) ? Math.round(d * 100) : 0);
const d = (cents) => cents / 100;
const has = (v) => typeof v === 'number' && Number.isFinite(v);
// Mirrors receipts-checks.js on the server; the forms come with each receipt
// (list -> checkForms).
export const GROSS_LINE_FORMS = ['lines_less_line_discounts', 'lines_less_line_discounts_plus_discount'];
export const DISCOUNT_IN_LINES_FORMS = ['lines_plus_discount', 'lines_less_line_discounts_plus_discount'];

/** How one receipt's lines are read: whether line discounts come off, and the discount on no line (cents). */
export function itemsBasis(receipt) {
  const f = receipt?.checkForms || {};
  const subtractLineDiscounts = GROSS_LINE_FORMS.includes(f.lines_vs_subtotal);
  const unallocatedCents = f.parts_vs_total === 'subtotal_less_discount' && !DISCOUNT_IN_LINES_FORMS.includes(f.lines_vs_subtotal) && has(receipt.discount) ? c(receipt.discount) : 0;
  return { subtractLineDiscounts, unallocatedCents };
}

/** What a line cost, in cents, under its receipt's basis. */
export function lineCostCents(line, basis) {
  return c(line.amount) - (basis?.subtractLineDiscounts ? c(line.discount) : 0);
}
const lineNetCents = (l) => lineCostCents(l, l.basis);

// The server's isTaxLine (receipts-checks.js), for lines that do not carry
// isTax (the receipt screen's detail lines): a short line naming a tax.
const TAX_LINE_RE = /^(?:[a-z&/-]+\s+){0,3}tax(?:es)?(?:\s+\d+(?:\.\d+)?\s?%)?$/i;
export const isTaxLine = (l) => (l?.isTax !== undefined ? l.isTax === true
  : !l?.synthetic && (String(l?.kindDetail || '').trim().toLowerCase() === 'tax' || TAX_LINE_RE.test(String(l?.description || '').replace(/\s+/g, ' ').trim())));
/** An item: not a payment's synthetic line, not a tax line. */
export const isItemLine = (l) => !l?.synthetic && !isTaxLine(l);

/** A single-charge payment (a tax payment, a fee, rent), not a purchase. */
export const isPayment = (r) => (r?.rowKind ? r.rowKind === 'payment' : r?.singleCharge === true);
/** -> { purchases, payments } in the order given. */
export function splitPayments(receipts) {
  const purchases = []; const payments = [];
  for (const r of receipts || []) (isPayment(r) ? payments : purchases).push(r);
  return { purchases, payments };
}

/** The Payments section: one row per payment, newest first. */
export function paymentRows(receipts) {
  return splitPayments(receipts).payments.map((r) => ({
    id: r.id, payee: r.merchant || 'Payee not read', purpose: r.paymentPurpose || null, date: r.purchaseDate || null, amount: has(r.total) ? r.total : null,
    accountLast4: r.payer?.accountLast4 || r.accountLast4 || null, owner: r.payer?.label || null,
    matchStatus: r.matchStatus ?? null, linkedAmount: r.linkedAmount ?? null, linkedHow: r.linkedHow ?? null, total: r.total,
  })).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(b.id).localeCompare(String(a.id)));
}

/** Whose receipt it is for the person filter: the payer's people when the server names them, else the receipt's person. */
const peopleOf = (r) => (Array.isArray(r?.payer?.people) && r.payer.people.length ? r.payer.people : [r?.person]).map((p) => String(p || '').toLowerCase());

/** Receipts in the filters. person: null = everyone. */
export function filterReceipts(receipts, { person = null, context = null, kind = null } = {}) {
  return (receipts || []).filter((r) => (!person || peopleOf(r).includes(person.toLowerCase()))
    && (!context || r.context === context)
    && (!kind || (r.lines || []).some((l) => kindOf(l) === kind)));
}

/**
 * The store a receipt is at: the server's merchantKey (receipts-merchants.js:
 * "COSTCO WHOLESALE" and "Costco" are one key), else its name lowercased, for
 * a receipt the merchants fixup has not reached. null when not read.
 */
export const storeKey = (r) => r?.merchantKey || (r?.merchant ? String(r.merchant).trim().toLowerCase() : '') || null;
const NOT_READ = 'Merchant not read';

/**
 * Names stores the way the server's totals?by=merchant does: the most common
 * name among a store's receipts, ties to the newest. see(r) each receipt
 * first, then name(storeKey).
 */
export function storeNamer() {
  const seen = new Map();
  return {
    see(r) {
      const k = storeKey(r);
      if (!k || !r.merchant) return;
      const names = seen.get(k) || new Map();
      const e = names.get(r.merchant) || { n: 0, date: '' };
      e.n++;
      if (String(r.purchaseDate || '') > e.date) e.date = String(r.purchaseDate || '');
      names.set(r.merchant, e);
      seen.set(k, names);
    },
    name(k) {
      if (!k || !seen.has(k)) return NOT_READ;
      let best = null; let be = null;
      for (const [n, e] of seen.get(k)) if (!be || e.n > be.n || (e.n === be.n && e.date > be.date)) { best = n; be = e; }
      return best;
    },
  };
}
/**
 * A receipt's names in either shape the server sends. The list sends merchant
 * (the name shown) + merchantPrinted; the detail sends merchant (as printed)
 * + merchantDisplay. -> { shown, printed }
 */
export function storeNames(r) {
  if (r?.merchantPrinted !== undefined) return { shown: r.merchant || null, printed: r.merchantPrinted || null };
  if (r?.merchantDisplay !== undefined) return { shown: r.merchantDisplay || r.merchant || null, printed: r.merchant || null };
  return { shown: r?.merchant || null, printed: r?.merchant || null };
}
/** The name as printed, when it differs from the name shown (case and spaces aside); else null. */
export function printedName(r) {
  const norm = (x) => String(x || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const { shown, printed } = storeNames(r);
  return printed && shown && norm(printed) !== norm(shown) ? printed : null;
}
const namerOf = (receipts) => { const nm = storeNamer(); for (const r of receipts || []) nm.see(r); return nm; };

/** Lines of the receipts, each tagged with its receipt. kind: keep only that kind. */
export function linesOf(receipts, kind = null) {
  const out = [];
  for (const r of receipts) {
    const basis = itemsBasis(r);
    // name comes from what was read, before an unlabeled kind counts as "other".
    for (const l of r.lines || []) if (isItemLine(l) && (!kind || kindOf(l) === kind)) out.push({ ...l, name: itemLabel(l), kind: kindOf(l), receipt: r, basis });
  }
  return out;
}

export function headline(receipts) {
  const totalCents = receipts.reduce((a, r) => a + c(r.total), 0);
  const stores = new Set(receipts.map(storeKey).filter(Boolean));
  const junk = linesOf(receipts, 'junk_food');
  const detail = [...new Set(junk.map((l) => l.kindDetail).filter(Boolean))];
  return {
    total: d(totalCents),
    receipts: receipts.length,
    stores: stores.size,
    totalsMissing: receipts.filter((r) => !has(r.total)).length,
    junk: { amount: d(junk.reduce((a, l) => a + lineNetCents(l), 0)), items: junk.length, details: detail },
  };
}

/** By kind: [{ kind, amount, share, lines, details:[{ detail, amount }] }], biggest first. */
export function byKind(receipts) {
  const m = new Map();
  let all = 0;
  for (const l of linesOf(receipts)) {
    const n = lineNetCents(l);
    all += n;
    const e = m.get(l.kind) || { kind: l.kind, cents: 0, lines: 0, details: new Map() };
    e.cents += n; e.lines++;
    const k = l.kindDetail || '(no detail)';
    const dd = e.details.get(k) || { cents: 0, items: [] };
    dd.cents += n;
    dd.items.push({ name: l.name, merchant: l.receipt.merchant ?? null, date: l.receipt.purchaseDate ?? null, amount: d(n), receiptId: l.receipt.id });
    e.details.set(k, dd);
    m.set(l.kind, e);
  }
  const rows = [...m.values()].map((e) => ({
    kind: e.kind, amount: d(e.cents), lines: e.lines, share: all > 0 ? e.cents / all : 0,
    details: [...e.details.entries()].map(([detail, x]) => ({ detail, amount: d(x.cents), items: x.items.sort((a, b) => b.amount - a.amount) })).sort((a, b) => b.amount - a.amount),
  })).sort((a, b) => b.amount - a.amount);
  const noLines = receipts.filter((r) => !(r.lines || []).length);
  const unallocated = receipts.reduce((a, r) => a + itemsBasis(r).unallocatedCents, 0);
  return { rows, itemsTotal: d(all), unallocatedDiscount: d(unallocated), receiptsWithoutLines: noLines.length, dollarsWithoutLines: d(noLines.reduce((a, r) => a + c(r.total), 0)) };
}

/** Real food vs junk, and what is counted on its own. */
export function realVsJunk(receipts) {
  const sums = { real: 0, junk: 0, separate: 0 };
  const sep = new Map();
  for (const l of linesOf(receipts)) {
    const g = groupOfKind(l.kind);
    sums[g] += lineNetCents(l);
    if (g === 'separate') sep.set(l.kind, (sep.get(l.kind) || 0) + lineNetCents(l));
  }
  const food = sums.real + sums.junk;
  return {
    real: d(sums.real), junk: d(sums.junk),
    realShare: food > 0 ? sums.real / food : 0, junkShare: food > 0 ? sums.junk / food : 0,
    separate: KIND_GROUPS.separate.filter((k) => sep.has(k)).map((k) => ({ kind: k, amount: d(sep.get(k)) })),
  };
}

/**
 * Who paid: by the card's person ("Unknown card" when the last four is not
 * on the card list), with each card by name: "Capital One Savor ·7502".
 */
export function whoPaid(receipts) {
  const m = new Map();
  for (const r of receipts) {
    const k = payerName(r) || (r.cardKnown === false ? 'Unknown card' : 'Not read');
    const e = m.get(k) || { person: k, cents: 0, receipts: 0, cards: new Set() };
    e.cents += c(r.total); e.receipts++;
    const t = cardText(r);
    if (t) e.cards.add(t);
    m.set(k, e);
  }
  return [...m.values()].map((e) => ({ person: e.person, amount: d(e.cents), receipts: e.receipts, cards: [...e.cards] })).sort((a, b) => b.amount - a.amount);
}

/** Where: by store (storeKey), trips = receipts. */
export function where(receipts) {
  const nm = namerOf(receipts);
  const m = new Map();
  for (const r of receipts) {
    const k = storeKey(r) || '';
    const e = m.get(k) || { merchant: nm.name(k), cents: 0, trips: 0 };
    e.cents += c(r.total); e.trips++;
    m.set(k, e);
  }
  return [...m.values()].map((e) => ({ merchant: e.merchant, amount: d(e.cents), trips: e.trips })).sort((a, b) => b.amount - a.amount);
}

/** Biggest items: lines merged by item name within a store (storeKey), biggest first. */
export function biggestItems(receipts, n = 8) {
  const nm = namerOf(receipts);
  const m = new Map();
  for (const l of linesOf(receipts)) {
    const { name } = l;
    const sk = storeKey(l.receipt) || '';
    const key = `${sk}|${name.toLowerCase()}`;
    const e = m.get(key) || { name, kind: l.kind, merchant: nm.name(sk), receiptIds: new Set(), cents: 0, count: 0 };
    e.cents += lineNetCents(l); e.count++; e.receiptIds.add(l.receipt.id);
    m.set(key, e);
  }
  return [...m.values()].map((e) => ({ name: e.name, kind: e.kind, merchant: e.merchant, amount: d(e.cents), count: e.count, receiptIds: [...e.receiptIds] }))
    .sort((a, b) => b.amount - a.amount).slice(0, n);
}

// Match states as the band shows them. "No bank match expected" (cash,
// Capital One) is its own segment, not a failure.
export const BAND_SEGMENTS = [
  { key: 'matched', label: 'matched', statuses: ['linked'] },
  { key: 'noBank', label: 'no bank match expected', statuses: ['no_bank_match_expected'] },
  { key: 'waiting', label: 'waiting for the charge', statuses: [null, 'pending', 'candidates', 'duplicate_suspected'] },
  { key: 'notFound', label: 'no charge found', statuses: ['no_charge_found'] },
];

/** Checked against Chase: dollars and receipts per segment. */
export function chaseBand(receipts) {
  const seg = Object.fromEntries(BAND_SEGMENTS.map((s) => [s.key, { key: s.key, label: s.label, cents: 0, receipts: 0 }]));
  for (const r of receipts) {
    const s = BAND_SEGMENTS.find((x) => x.statuses.includes(r.matchStatus ?? null)) || BAND_SEGMENTS[2];
    seg[s.key].cents += c(r.total); seg[s.key].receipts++;
  }
  const total = Object.values(seg).reduce((a, s) => a + s.cents, 0);
  return { total: d(total), segments: BAND_SEGMENTS.map((s) => ({ ...seg[s.key], amount: d(seg[s.key].cents), share: total > 0 ? seg[s.key].cents / total : 0 })) };
}

/** Month by month: only months that have receipts, oldest first. */
export function months(receipts) {
  const m = new Map();
  for (const r of receipts) {
    const k = String(r.purchaseDate || '').slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(k)) continue;
    const e = m.get(k) || { month: k, cents: 0, receipts: 0 };
    e.cents += c(r.total); e.receipts++;
    m.set(k, e);
  }
  return [...m.values()].sort((a, b) => a.month.localeCompare(b.month)).map((e) => ({ month: e.month, amount: d(e.cents), receipts: e.receipts }));
}

/**
 * Items + tax (+ tip) against the receipts' own totals, in dollars:
 * { items, unallocated, tax, tip, sum, onReceipts, off: [receipt ids that do not reconcile] }.
 * Receipts with no lines read are left out of items and named in noLines.
 */
export function reconcile(receipts) {
  let items = 0; let unallocated = 0; let tax = 0; let tip = 0; let total = 0;
  const off = []; const noLines = [];
  for (const r of receipts) {
    total += c(r.total);
    tax += c(r.tax); tip += c(r.tip);
    const itemLines = (r.lines || []).filter(isItemLine);
    if (!itemLines.length) { noLines.push(r.id); continue; }
    const b = itemsBasis(r);
    // Tax lines are in r.tax already: counted once, as tax.
    const it = itemLines.reduce((a, l) => a + lineCostCents(l, b), 0);
    items += it; unallocated += b.unallocatedCents;
    if (Math.abs(it - b.unallocatedCents + c(r.tax) + c(r.tip) - c(r.total)) > 1) off.push(r.id);
  }
  return { items: d(items), unallocated: d(unallocated), tax: d(tax), tip: d(tip), sum: d(items - unallocated + tax + tip), onReceipts: d(total), off, noLines };
}

/** This trip by kind, for one receipt's lines (receipt: its checkForms decide line discounts). */
export function tripByKind(lines, receipt = null) {
  const basis = itemsBasis(receipt);
  const m = new Map();
  for (const l of (lines || []).filter(isItemLine)) { const k = kindOf(l); m.set(k, (m.get(k) || 0) + lineCostCents(l, basis)); }
  const all = [...m.values()].reduce((a, n) => a + n, 0);
  return KINDS.filter((k) => m.has(k)).map((k) => ({ kind: k, amount: d(m.get(k)), share: all > 0 ? m.get(k) / all : 0 })).sort((a, b) => b.amount - a.amount);
}

/** YYYY-MM-DD first and last day of the month holding `today`. */
export function thisMonth(today = new Date()) {
  const y = today.getFullYear(); const mo = today.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const last = new Date(y, mo + 1, 0).getDate();
  return { from: `${y}-${pad(mo + 1)}-01`, to: `${y}-${pad(mo + 1)}-${pad(last)}` };
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Tax, as read. Only a receipt with both a total and a tax line counts:
 * items before tax = total − tax − tip (a tip only where printed). A receipt
 * with no tax line (fuel) shows "no tax line", is never $0.00 tax, and stays
 * out of the rate. A receipt with no total is left out entirely.
 * Tips on those receipts are their own figure, so items + tax + tips + no
 * tax line = the total of every receipt with one.
 * -> { items, tax, rate, receipts, tips: { receipts, total }, noTaxLine: { receipts, total }, noTotal,
 *      byStore: [{ merchant, items, tax, rate, receipts, noTaxLine }],
 *      byReceipt: [{ id, merchant, date, total, tax|null, items|null, rate|null }] }
 */
export function taxBreakdown(receipts) {
  let items = 0; let tax = 0; let taxed = 0; let noTaxN = 0; let noTaxCents = 0; let noTotal = 0; let tips = 0; let tipsN = 0;
  const nm = namerOf(receipts);
  const stores = new Map();
  const byReceipt = [];
  for (const r of receipts || []) {
    const store = storeKey(r) || '';
    const st = stores.get(store) || { merchant: nm.name(store), items: 0, tax: 0, receipts: 0, noTaxLine: 0 };
    if (!isNum(r.total)) { noTotal++; byReceipt.push({ id: r.id, merchant: r.merchant, date: r.purchaseDate, total: null, tax: isNum(r.tax) ? r.tax : null, items: null, rate: null }); continue; }
    if (!isNum(r.tax)) {
      noTaxN++; noTaxCents += c(r.total); st.noTaxLine++; stores.set(store, st);
      byReceipt.push({ id: r.id, merchant: r.merchant, date: r.purchaseDate, total: r.total, tax: null, items: null, rate: null });
      continue;
    }
    const it = c(r.total) - c(r.tax) - (isNum(r.tip) ? c(r.tip) : 0);
    items += it; tax += c(r.tax); taxed++;
    if (isNum(r.tip)) { tips += c(r.tip); tipsN++; }
    st.items += it; st.tax += c(r.tax); st.receipts++; stores.set(store, st);
    byReceipt.push({ id: r.id, merchant: r.merchant, date: r.purchaseDate, total: r.total, tax: r.tax, items: d(it), rate: it > 0 ? c(r.tax) / it : null });
  }
  return {
    items: d(items), tax: d(tax), rate: items > 0 ? tax / items : null, receipts: taxed, tips: { receipts: tipsN, total: d(tips) },
    noTaxLine: { receipts: noTaxN, total: d(noTaxCents) }, noTotal,
    byStore: [...stores.values()].map((s) => ({ merchant: s.merchant, items: d(s.items), tax: d(s.tax), rate: s.items > 0 ? s.tax / s.items : null, receipts: s.receipts, noTaxLine: s.noTaxLine })).sort((a, b) => b.tax - a.tax),
    byReceipt: byReceipt.sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
  };
}
